import React, { useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import Chip from '@material-ui/core/Chip';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { aggregatedSurveyResponsesForSurveyIdSelector } from './survey-response-selector';
import { loadEmployeeGroups } from '../../employee-groups/actions/load-employee-groups';
import { employeeGroupsForOrgIdSelector } from '../../employee-groups/employee-group-selectors';
import { userSelector } from '../../reducers/current-user-selectors';
import { employeesForOrgIdSelector } from '../../reducers/employee-selectors';
import {
  EMPLOYEE_STATUS,
  EmployeeStatus,
  loadOrgEmployeesByStatus,
} from '../actions/load-org-employees-by-status';
import { loadSurveyResponses } from '../actions/survey/load-survey-responses';
import { loadSurveys } from '../actions/survey/load-surveys';

import { allEmployeesKey } from '../../util/all-employees-option';
import { SURVEY_TYPES } from '../../util/survey-types';
import { employeeName } from '../utils/employee-name';
import { getEmployeeGroupsFromEmployees } from '../utils/get-employee-groups-from-employees';

const mapStateToProps = (state: any, { orgId, surveyId }: InputProps) => ({
  allEmployeeGroups: employeeGroupsForOrgIdSelector(state, orgId),
  employees: employeesForOrgIdSelector(state, orgId),
  user: userSelector(state),
  surveyResponses: surveyId ? aggregatedSurveyResponsesForSurveyIdSelector(state, surveyId) : null,
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId, surveyId }: InputProps,
) => ({
  actions: {
    loadOrgEmployeeGroups: () => dispatch(loadEmployeeGroups(orgId)),
    loadEmployees: (employeeStatus: EmployeeStatus) =>
      dispatch(loadOrgEmployeesByStatus(orgId, employeeStatus)),
    loadSurveyResponses: () => (surveyId ? dispatch(loadSurveyResponses(surveyId)) : null),
    loadSurveys: () => (surveyId ? dispatch(loadSurveys(orgId)) : null),
  },
});

type InputProps = {
  orgId: number;
  surveyId?: number;
  selectedEmployees: Array<Employee> | null;
  selectedEmployeeGroup: EmployeeGroupWithAllOption | null;
  onEmployeeGroupSelect: (employeeGroup: EmployeeGroupWithAllOption | null) => void;
  onEmployeeSelect: (employee: Array<Employee> | null) => void;
  surveyType: SurveyType;
};

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

export const SurveyRecipientSelector = connect(
  mapStateToProps,
  mapDispatchToProps,
)((props: Props) => {
  const {
    actions,
    orgId,
    surveyId,
    onEmployeeGroupSelect,
    onEmployeeSelect,
    employees,
    selectedEmployees,
    selectedEmployeeGroup,
    surveyResponses,
    surveyType,
    allEmployeeGroups,
  } = props;

  const [selectionType, setSelectionType] = useState<'GROUP' | 'EMPLOYEE'>(
    surveyId ? 'EMPLOYEE' : 'GROUP',
  );

  useEffect(() => {
    actions.loadOrgEmployeeGroups();
    actions.loadEmployees(EMPLOYEE_STATUS.CURRENT);
    actions.loadEmployees(EMPLOYEE_STATUS.FORMER);
    actions.loadSurveys();
    actions.loadSurveyResponses();
  });

  // We allow users to send the exit survey to current and terminated employees so we don't have to care about the order of actions taken in TE
  const filterTerminatedEmployees = surveyType !== SURVEY_TYPES.EXIT;
  const allPossibleEmployees = filterTerminatedEmployees
    ? employees.filter((e) => e.isCurrentEmployee)
    : employees;

  const groupByMethod = (e: Employee) =>
    e.isCurrentEmployee ? 'Current Employees' : 'Former Employees';

  const employeeGroups = getEmployeeGroupsFromEmployees(allPossibleEmployees, allEmployeeGroups);
  const allOption = {
    employeeGroupId: allEmployeesKey(orgId),
    name: 'All Employees',
  };
  const sortedEmployeeGroupsWithAllOption: Array<EmployeeGroupWithAllOption> = [
    ...employeeGroups,
    allOption,
  ].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

  const sortedEmployees = allPossibleEmployees
    .filter((employee) =>
      // This is for the case where we're adding employees - if an employee already has a SurveyResponse, they should not be an option to add
      surveyResponses ? !surveyResponses.find((sr) => sr.employeeId === employee.employeeId) : true,
    )
    .sort((a, b) => {
      if (a.isCurrentEmployee && !b.isCurrentEmployee) {
        return -1;
      }
      if (!a.isCurrentEmployee && b.isCurrentEmployee) {
        return 1;
      }
      return employeeName(a).toLowerCase().localeCompare(employeeName(b).toLowerCase());
    });

  const handleGroupFormChange = (e: React.ChangeEvent<any>, newValue?: any) => {
    const { value } = e.target;
    onEmployeeGroupSelect(newValue || value);
  };

  const handleEmployeeFormChange = (e: React.ChangeEvent<any>, newValue?: any) => {
    const { value } = e.target;
    onEmployeeSelect(newValue || value);
  };

  const handleSwitchType = () => {
    onEmployeeGroupSelect(null);
    onEmployeeSelect(null);
    if (selectionType === 'GROUP') {
      setSelectionType('EMPLOYEE');
    } else if (selectionType === 'EMPLOYEE') {
      setSelectionType('GROUP');
    }
  };

  return (
    <>
      {selectionType === 'GROUP' ? (
        <Autocomplete
          className="u-margin-bottom-md"
          size="small"
          options={sortedEmployeeGroupsWithAllOption}
          value={selectedEmployeeGroup ?? allOption}
          getOptionLabel={(option) => option.name}
          onChange={handleGroupFormChange}
          renderInput={(params) => <TextField {...params} label="Group" fullWidth />}
        />
      ) : (
        <Autocomplete
          multiple
          className="u-margin-bottom-md"
          size="small"
          options={sortedEmployees}
          value={selectedEmployees ?? []}
          groupBy={filterTerminatedEmployees ? undefined : groupByMethod}
          getOptionLabel={(option) => employeeName(option)}
          renderTags={(value: Employee[], getTagProps) =>
            value.map((option: Employee, index: number) => (
              <Chip variant="outlined" label={employeeName(option)} {...getTagProps({ index })} />
            ))
          }
          onChange={handleEmployeeFormChange}
          renderInput={(params) => <TextField {...params} label="Employee" fullWidth />}
        />
      )}
      {surveyId === undefined ? (
        <div
          role="presentation"
          onClick={handleSwitchType}
          className="typo-subtitle typo-link u-float-right"
        >
          Select {selectionType === 'GROUP' ? 'individual employees' : 'a group'} instead
        </div>
      ) : null}
    </>
  );
});
